import { apiService } from '../api/api-client';
import {
  AgreementFormEntry,
  AgreementPayloadFields,
  Counterparty,
  CounterpartyAgreement,
  CounterpartyListRow,
  CounterpartyOptionPage,
  CounterpartyListPage,
  RepoMarginMethodology
} from '@/types/counterparty.types';
import { PaginatedResponse } from '@/types/common.types';
import type { FrameworkAgreement } from '@/types/trades.types';

export interface AgreementApiResponse {
  id: number;
  type: FrameworkAgreement;
  currency: string;
  agreement_date: string | null;
  expiry_date: string | null;
}

interface CounterpartyApiResponse {
  id: number;
  name: string;
  client_id: string;
  country?: string;
  is_active?: boolean;
  credit_rating?: string;
  notes?: string;
  agreements?: AgreementApiResponse[];
  parent?: number | null;
  parent_name?: string | null;
  /** Detail only. */
  group_member_count?: number;
  // LR-389: DRF DecimalField serialises to a string (e.g. "50000.00"); null = no MTA.
  minimum_transfer_amount?: string | null;
  minimum_transfer_amount_currency?: string;
  // LR-579: repo margin call methodology (Method A / Method B).
  repo_margin_methodology?: RepoMarginMethodology;
}

export const agreementPayloadFields = (
  entry: Pick<AgreementFormEntry, 'currency' | 'validFrom' | 'validTo' | 'isPerpetual'>,
): AgreementPayloadFields => ({
  currency: entry.currency,
  agreement_date: entry.validFrom || null,
  expiry_date: entry.isPerpetual ? null : entry.validTo || null,
});

const toAgreementPayload = (entry: AgreementFormEntry) => ({
  type: entry.type,
  ...agreementPayloadFields(entry),
});

/** A stored agreement's fields that differ from what was loaded; empty when none do. */
const changedAgreementFields = (entry: AgreementFormEntry): Partial<AgreementPayloadFields> => {
  const fields = agreementPayloadFields(entry);
  return Object.fromEntries(
    (Object.keys(fields) as (keyof AgreementPayloadFields)[])
      .filter((key) => !entry.stored || fields[key] !== entry.stored[key])
      .map((key) => [key, fields[key]]),
  );
};

/** Parse the API's DecimalField MTA string to a number; null/undefined → null (no threshold). */
const parseMta = (value: string | null | undefined): number | null =>
  value != null ? Number(value) : null;

// Mock data generator for fields not in the current API
const mockAgreementData = (id: number): CounterpartyAgreement[] => [
  {
    id: `agr-${id}-1`,
    type: id % 2 === 0 ? 'gmsla' : 'gmra',
    currency: 'USD',
    agreementId: `AGR-${1000 + id}`,
    validFrom: '2023-01-01',
    validTo: id % 3 === 0 ? '2026-06-01' : undefined,
    isPerpetual: id % 3 !== 0,
    status: 'ACTIVE',
    settlement: 'T+1',
    recallNotice: 'T+3',
    deliveryType: 'dvp',
    marginType: 'Cash',
    venue: 'OTC',
    dividendType: 'Pass-through',
    collateralType: 'cash',
    governingLaw: 'English Law',
    history: []
  }
];

/**
 * The counterparty's agreements from the API (type, currency, dates), with the mock
 * generator filling only the terms the API does not expose yet (settlement, recall, …).
 */
export const buildAgreements = (
  cp: Pick<CounterpartyApiResponse, 'id' | 'agreements'>,
): CounterpartyAgreement[] => {
  const base = mockAgreementData(cp.id)[0];
  return (cp.agreements ?? []).map((agreement) => ({
    ...base,
    id: String(agreement.id),
    apiId: agreement.id,
    type: agreement.type,
    currency: agreement.currency,
    validFrom: agreement.agreement_date ?? '',
    validTo: agreement.expiry_date ?? undefined,
    isPerpetual: !agreement.expiry_date,
  }));
};

/** One `/counterparties/` row → a register `CounterpartyListRow`. */
const toListRow = (cp: CounterpartyApiResponse): CounterpartyListRow => {
  const agreements = buildAgreements(cp);
  // The register's single-value columns (status, days remaining) read the agreement that expires
  // first, so a second agreement's expiry is not hidden behind a perpetual one; a counterparty
  // without any keeps a placeholder so they render.
  const expiring = agreements
    .filter((agreement) => agreement.validTo)
    .sort((a, b) => (a.validTo ?? '').localeCompare(b.validTo ?? ''));
  const primaryAgreement = expiring[0] ?? agreements[0] ?? {
    // No agreement: blank terms, never the mock's dates (a made-up expiry would show as overdue).
    ...mockAgreementData(cp.id)[0],
    currency: '',
    validFrom: '',
    validTo: undefined,
    isPerpetual: true,
  };

  // Calculate days remaining
  let daysRemaining = null;
  if (primaryAgreement.validTo) {
    const expiry = new Date(primaryAgreement.validTo);
    const today = new Date();
    daysRemaining = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  }

  return {
    id: cp.id,
    name: cp.name,
    clientId: cp.client_id,
    legalEntity: cp.country || 'International',
    isActive: Boolean(cp.is_active),
    creditRating: cp.credit_rating,
    country: cp.country,
    notes: cp.notes,
    minimumTransferAmount: parseMta(cp.minimum_transfer_amount),
    minimumTransferAmountCurrency: cp.minimum_transfer_amount_currency,
    repoMarginMethodology: cp.repo_margin_methodology,
    parentId: cp.parent ?? null,
    parentName: cp.parent_name ?? null,
    agreements,
    primaryAgreement,
    daysRemaining,
    statusBadge: primaryAgreement.status,
  };
};

export const counterpartyService = {
  /**
   * LR-572: one page of counterparties for a picker, searched server-side.
   *
   * Deliberately not `getCounterparties`: that one derives a CounterpartyListRow
   * per row (agreement stub, expiry arithmetic) and returns a bare array,
   * dropping the `count` that paging needs. A selector wants neither — it
   * wants four fields and a total.
   */
  async searchCounterparties(params: {
    search?: string;
    limit: number;
    offset: number;
    /** Registry-wide by default; `true` narrows to the active slice server-side. */
    activeOnly?: boolean;
  }): Promise<CounterpartyOptionPage> {
    const { search, limit, offset, activeOnly } = params;
    const response = await apiService.get<PaginatedResponse<CounterpartyApiResponse>>(
      '/counterparties/',
      {
        params: {
          // DRF's SearchFilter no-ops on empty terms, so `search=` would be
          // harmless — omitted anyway so the request says what it means.
          ...(search ? { search } : {}),
          ...(activeOnly ? { is_active: true } : {}),
          // Live counterparties first, alphabetical within each group (LR-572 dp #3):
          // the registry runs to ~16k rows on the stands with ~40 active, so plain
          // `name` order fills the first page with deactivated rows nobody bills.
          // `name` is unique, so the order is total and offset paging stays stable.
          ordering: '-is_active,name',
          limit,
          offset
        }
      }
    );

    return {
      count: response.count,
      results: response.results.map((cp) => ({
        id: cp.id,
        name: cp.name,
        isActive: Boolean(cp.is_active),
        parentId: cp.parent ?? null,
      }))
    };
  },

  /**
   * Get list of counterparties
   */
  async getCounterparties(params?: Record<string, unknown>): Promise<CounterpartyListRow[]> {
    const response = await apiService.get<PaginatedResponse<CounterpartyApiResponse>>('/counterparties/', { params });
    return response.results.map(toListRow);
  },

  /**
   * One page of the register, `count` kept — the total is what getCounterparties
   * drops, and without it the table cannot page (LR-661).
   */
  async getCounterpartiesPage(params: Record<string, unknown>): Promise<CounterpartyListPage> {
    const response = await apiService.get<PaginatedResponse<CounterpartyApiResponse>>('/counterparties/', { params });
    return {
      count: response.count,
      results: response.results.map(toListRow),
    };
  },

  /**
   * Get counterparty details
   */
  async getCounterpartyDetail(id: number): Promise<Counterparty> {
    const cp = await apiService.get<CounterpartyApiResponse>(`/counterparties/${id}/`);

    return {
      id: cp.id,
      name: cp.name,
      clientId: cp.client_id,
      legalEntity: cp.country || 'International',
      isActive: Boolean(cp.is_active),
      // LR-440: map the detail-only fields the list endpoint omits (notes) plus the
      // ones it does expose, so the edit form can prefill from a full counterparty
      // instead of blanking them on save.
      creditRating: cp.credit_rating,
      country: cp.country,
      notes: cp.notes,
      minimumTransferAmount: parseMta(cp.minimum_transfer_amount),
      minimumTransferAmountCurrency: cp.minimum_transfer_amount_currency,
      repoMarginMethodology: cp.repo_margin_methodology,
      parentId: cp.parent ?? null,
      parentName: cp.parent_name ?? null,
      groupMemberCount: cp.group_member_count ?? 0,
      agreements: buildAgreements(cp),
    };
  },

  /**
   * Create new agreement
   */
  async createAgreement(counterpartyId: number, data: Record<string, unknown>): Promise<unknown> {
    // If counterpartyId is 0, it means we are creating a new counterparty
    if (counterpartyId === 0) {
      const agreements = (data.agreements as AgreementFormEntry[] | undefined) ?? [];
      const response = await apiService.post<CounterpartyApiResponse>('/counterparties/', {
        name: data.name,
        client_id: data.clientId,
        agreements: agreements.filter((entry) => entry.enabled).map(toAgreementPayload),
        parent: data.parentId ?? null,
        is_active: true,
        credit_rating: data.creditRating,
        country: data.country,
        notes: data.notes,
        // LR-389: MTA is optional — empty input maps to null (no threshold).
        minimum_transfer_amount:
          data.minimumTransferAmount === '' || data.minimumTransferAmount == null
            ? null
            : data.minimumTransferAmount,
        minimum_transfer_amount_currency: data.minimumTransferAmountCurrency || 'USD',
        // LR-579: omitted → the model default (Method B, the usual market practice).
        repo_margin_methodology: data.repoMarginMethodology || undefined
      });
      return response;
    }

    // Mock implementation for existing counterparty agreement (not in current API)
    return {
      id: 'new-agr-' + Math.random().toString(36).substr(2, 9),
      ...data,
      status: 'PENDING_APPROVAL',
      history: []
    };
  },

  /**
   * Update an existing counterparty (LR-440).
   *
   * PATCH /counterparties/{id}/ with the editable fields mapped camelCase → snake_case.
   * `client_id` is immutable: it is omitted here and is not part of the update
   * serializer's fields, so the backend leaves it unchanged.
   */
  async updateAgreement(counterpartyId: number, data: Record<string, unknown>): Promise<CounterpartyApiResponse> {
    const payload: Record<string, unknown> = {
      name: data.name,
      parent: data.parentId ?? null,
      is_active: data.isActive,
      credit_rating: data.creditRating,
      country: data.country,
      // LR-389: MTA is optional — empty input maps to null (no threshold).
      minimum_transfer_amount:
        data.minimumTransferAmount === '' || data.minimumTransferAmount == null
          ? null
          : data.minimumTransferAmount,
      minimum_transfer_amount_currency: data.minimumTransferAmountCurrency || 'USD'
    };
    // LR-579: only PATCH the methodology when the caller has an authoritative value, so a form
    // that never loaded it can't reset a counterparty's election (same rule as `notes` below).
    if (data.repoMarginMethodology !== undefined) {
      payload.repo_margin_methodology = data.repoMarginMethodology;
    }
    // LR-440: `notes` is not on the list endpoint, so the edit modal can only supply
    // it once the full counterparty has loaded. When the caller has no authoritative
    // value it leaves `notes` out entirely; omitting it from the PATCH makes DRF's
    // partial_update keep the stored notes instead of blanking them.
    if (data.notes !== undefined) {
      payload.notes = data.notes;
    }
    // One PATCH carries the record and the agreements the form changed, so the server writes all
    // of it or none. An agreement is never removed here (pools and trades hang off it).
    const agreements = (data.agreements as AgreementFormEntry[] | undefined) ?? [];
    const changed = agreements.filter((entry) => entry.enabled).flatMap((entry): Record<string, unknown>[] => {
      if (entry.apiId == null) return [toAgreementPayload(entry)];
      const fields = changedAgreementFields(entry);
      return Object.keys(fields).length > 0 ? [{ id: entry.apiId, ...fields }] : [];
    });
    if (changed.length > 0) {
      payload.agreements = changed;
    }
    return apiService.patch<CounterpartyApiResponse>(`/counterparties/${counterpartyId}/`, payload);
  }
};
